import { InviteDialog } from '@/components/team/InviteDialog'
import { useTeamLabels } from '@/components/team/labels'
import { PersonAvatar } from '@/components/team/PersonAvatar'
import { useTeam } from '@/hooks/useTeam'
import { Button } from '@repo/ui/components'
import type { RosterPerson } from '@repo/zod-schemas/supervisor-roster'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertCircle, Loader2, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_contractor/team/')({
  component: TeamListPage,
})

const getStatusBadgeClasses = (person: RosterPerson) =>
  person.signedIn || person.isSelf ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'

const HEADER_CELL = 'h-10 px-4 text-left align-middle font-semibold text-muted-foreground'
const BODY_CELL = 'p-4 py-2 align-middle whitespace-nowrap'

function TeamListPage() {
  const { t } = useTranslation('team')
  const navigate = useNavigate()
  const labels = useTeamLabels()
  const { data, isLoading, isError, refetch } = useTeam()
  const people = data?.people ?? []

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      )
    }
    if (isError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
          <AlertCircle className="size-12 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('list.error.title')}</p>
          <Button variant="outline" size="sm" onPress={() => void refetch()}>
            {t('list.retry')}
          </Button>
        </div>
      )
    }
    return (
      <div className="space-y-6">
        <div className="overflow-hidden rounded-lg border bg-card">
          <div className="relative w-full overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <thead className="bg-muted/50 [&_tr]:border-b">
                <tr className="border-b transition-colors">
                  <th className={HEADER_CELL}>{t('list.columns.name')}</th>
                  <th className={HEADER_CELL}>{t('list.columns.role')}</th>
                  <th className={HEADER_CELL}>{t('list.columns.projects')}</th>
                  <th className={HEADER_CELL}>{t('list.columns.status')}</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {people.map((person, index) => (
                  <tr
                    key={person.userId}
                    className={`cursor-pointer border-b transition-colors hover:bg-muted/50 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}
                    onClick={() =>
                      void navigate({ to: '/team/$userId', params: { userId: person.userId } })
                    }
                  >
                    <td className={BODY_CELL}>
                      <div className="flex items-center gap-3">
                        <PersonAvatar person={person} />
                        <div className="min-w-0">
                          <p className="font-medium">{labels.name(person)}</p>
                          <p className="truncate text-xs text-muted-foreground">{person.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className={BODY_CELL}>{labels.role(person.role)}</td>
                    <td className={BODY_CELL}>
                      <span className="text-muted-foreground">
                        {person.jobs.length > 0
                          ? t('list.jobs', { count: person.jobs.length })
                          : t('list.noJobs')}
                      </span>
                    </td>
                    <td className={BODY_CELL}>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeClasses(person)}`}
                      >
                        {labels.status(person)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* The caller is always on their own roster, so "empty" means nobody else. */}
        {people.every((person) => person.isSelf) && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <Users className="size-12 text-muted-foreground" />
            <p className="font-medium">{t('list.empty.title')}</p>
            <p className="text-sm text-muted-foreground">{t('list.empty.description')}</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('list.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('list.subtitle')}</p>
        </div>
        <InviteDialog />
      </header>

      {data && (
        <div className="text-sm text-muted-foreground">
          {t('list.count', { count: people.length })}
        </div>
      )}

      {renderBody()}
    </div>
  )
}
